export type Mode = 'mandelbrot' | 'julia';

export const MODES: readonly Mode[] = ['mandelbrot', 'julia'];

export interface View {
  cx: number;
  cy: number;
  scale: number;
  maxIter: number;
  mode: Mode;
  jcx: number;
  jcy: number;
  hue: number;
}

const HOME: Record<Mode, { cx: number; cy: number; span: number }> = {
  mandelbrot: { cx: -0.6, cy: 0, span: 3.2 },
  julia: { cx: 0, cy: 0, span: 3.2 },
};
const JULIA_C = { x: -0.8, y: 0.156 };
const MAX_ZOOM_OUT = 4;

export const ZOOM_STEP = 1.6;

export const initialView: View = {
  cx: HOME.mandelbrot.cx,
  cy: HOME.mandelbrot.cy,
  scale: 0,
  maxIter: 250,
  mode: 'mandelbrot',
  jcx: JULIA_C.x,
  jcy: JULIA_C.y,
  hue: 0,
};

export type Action =
  | { type: 'fit'; width: number }
  | { type: 'pan'; dx: number; dy: number }
  | { type: 'zoomAt'; x: number; y: number; width: number; height: number; factor: number }
  | { type: 'maxIter'; value: number }
  | { type: 'hue'; value: number }
  | { type: 'mode'; value: Mode; width: number }
  | { type: 'reset'; width: number };

export function fitScale(mode: Mode, width: number): number {
  return HOME[mode].span / Math.max(width, 1);
}

export function zoomOf(view: View, width: number): number {
  return view.scale > 0 ? fitScale(view.mode, width) / view.scale : 1;
}

export function formatZoom(zoom: number): string {
  if (zoom >= 100) return zoom.toFixed(0);
  if (zoom >= 1) return zoom.toFixed(1);
  return zoom.toFixed(2);
}

export function reducer(view: View, action: Action): View {
  switch (action.type) {
    case 'fit':
      return view.scale > 0 ? view : { ...view, scale: fitScale(view.mode, action.width) };
    case 'pan':
      return { ...view, cx: view.cx - action.dx * view.scale, cy: view.cy - action.dy * view.scale };
    case 'zoomAt': {
      const { x, y, width, height, factor } = action;
      const planeX = view.cx + (x - width / 2) * view.scale;
      const planeY = view.cy + (y - height / 2) * view.scale;
      const nextScale = Math.min(view.scale * factor, fitScale(view.mode, width) * MAX_ZOOM_OUT);
      return {
        ...view,
        scale: nextScale,
        cx: planeX - (x - width / 2) * nextScale,
        cy: planeY - (y - height / 2) * nextScale,
      };
    }
    case 'maxIter':
      return { ...view, maxIter: action.value };
    case 'hue':
      return { ...view, hue: action.value };
    case 'mode':
      return {
        ...view,
        mode: action.value,
        cx: HOME[action.value].cx,
        cy: HOME[action.value].cy,
        scale: fitScale(action.value, action.width),
      };
    case 'reset':
      return {
        ...view,
        cx: HOME[view.mode].cx,
        cy: HOME[view.mode].cy,
        scale: fitScale(view.mode, action.width),
        hue: 0,
      };
    default:
      return view;
  }
}
